import dayjs from 'dayjs';
import { AthletePackageAllLimitedCreditsUsed } from 'types';

import { parseDateTimeToDate } from './date-functions';

export type LastCreditAlertAudience = 'athlete' | 'staff';

export interface LastCreditAlertContent {
  title: string;
  subtitle: string;
  description: string;
}

interface BuildLastCreditAlertOptions {
  firstName: string;
  audience: LastCreditAlertAudience;
  timezone?: string;
  dateFormat: string;
  now?: Date;
}

const STAFF_RENEW_DESCRIPTION =
  'If the athlete no longer wants this package to renew, please turn off auto-renew or cancel the package from their profile.';
const ATHLETE_RENEW_DESCRIPTION = 'If you don’t want it to renew, please contact gym staff.';

const pluralizeDays = (days: number): string => `${days} ${days === 1 ? 'day' : 'days'}`;

/**
 * Builds the alert shown when a check-in or attendance change uses the last credit of a package,
 * or returns null when no alert applies.
 */
export const buildLastCreditAlert = (
  result: AthletePackageAllLimitedCreditsUsed,
  { firstName, audience, timezone, dateFormat, now = new Date() }: BuildLastCreditAlertOptions,
): LastCreditAlertContent | null => {
  const isStaff = audience === 'staff';
  const isTimeOrCreditBased = result.packageType === 'TIME_OR_CREDIT_BASED';

  if (!result.allLimitedCreditsUsed && !result.packageEnds) {
    return null;
  }

  if (!isTimeOrCreditBased) {
    return {
      title: isStaff
        ? `${firstName}’s package will renew tonight!`
        : `${firstName}, your package will renew tonight!`,
      subtitle: isStaff
        ? 'The athlete has used all the credits in their package. A new package will be generated tonight.'
        : 'You’ve used all the credits in your current package. A new package will be generated tonight.',
      description: isStaff ? STAFF_RENEW_DESCRIPTION : ATHLETE_RENEW_DESCRIPTION,
    };
  }

  const endDate = result.endDate ? parseDateTimeToDate(result.endDate, timezone) : null;
  const today = parseDateTimeToDate(now.toISOString(), timezone);
  const daysLeft = endDate ? Math.max(dayjs(endDate).diff(dayjs(today), 'day'), 0) : null;
  const timeLeft = daysLeft ? ` with ${pluralizeDays(daysLeft)} still left` : '';
  const renewalRule = endDate
    ? `It renews when its credits are used or on ${dayjs(endDate).format(dateFormat)}, whichever comes first`
    : 'It renews when its credits are used';

  if (result.packageEnds) {
    return {
      title: isStaff ? `${firstName}’s package is ending` : `${firstName}, your package is ending`,
      subtitle: isStaff
        ? `${firstName} has used all the credits in their package${timeLeft}. Auto Renew is off, so the package ends once their last booked session is over.`
        : `You’ve used all the credits in your current package${timeLeft}. Auto Renew is off, so the package ends once your last booked session is over.`,
      description: isStaff
        ? 'To keep them training, assign a new package from their profile.'
        : 'To keep training, please contact gym staff about a new package.',
    };
  }

  return {
    title: isStaff
      ? `${firstName}’s next package starts early!`
      : `${firstName}, your next package starts early!`,
    subtitle: isStaff
      ? `${firstName} has used all the credits in their package${timeLeft}. ${renewalRule}, so the next package starts once their last booked session is over.`
      : `You’ve used all the credits in your current package${timeLeft}. ${renewalRule}, so your next package starts once your last booked session is over.`,
    description: isStaff ? STAFF_RENEW_DESCRIPTION : ATHLETE_RENEW_DESCRIPTION,
  };
};
